import { AppPermissions } from '../../constants/Permissions';
import { dateHeader, formatCellDate } from './timeModeCell';
import { authorizedLinkTo, linkTo } from '../linkTo';
import AuthService from '../../services/auth.service';

export interface SightingListColumnOptions {
  hideReader?: boolean;
}

export const makeSightingListColumn = ({ hideReader = false }: SightingListColumnOptions = {}) => {
  // External users have no access to the permission-guarded /devices and /equipment
  // routes, so their links point at the device's visits instead. `bySerial=true` is
  // what tells the visits screen to look the device up by serial number rather than
  // by device id -- the same url the global search builds for an external user's
  // serial number search.
  const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

  const col = [
    {
      header: 'DeviceId',
      accessorKey: 'deviceId',
      enableColumnFilter: true,
      size: 160,
      style: { textAlign: 'center' },
      cell: ({ row }) => (
        <span style={{ paddingRight: '24px' }}>{linkTo(isExternalUser ? '/visits' : '/devices', row.original.deviceId, row.original.deviceId)}</span>
      ),
    },
    {
      header: 'Serial Number',
      accessorKey: 'serialNumber',
      enableColumnFilter: true,
      size: 110,
      style: { textAlign: 'center' },
      cell: ({ row }) => {
        const serialNumber = row.original.serialNumber;
        if (!serialNumber) return '';

        return isExternalUser
          ? linkTo('/visits', `${encodeURIComponent(serialNumber)}?bySerial=true`, serialNumber)
          : linkTo('/equipment', row.original.equipmentId, serialNumber);
      },
    },

    {
      header: 'Station',
      accessorKey: 'stationIataCode',
      enableColumnFilter: true,
      size: 65,
      style: { textAlign: 'center' },
    },
    {
      header: 'Facility',
      accessorKey: 'closestFacilityName',
      size: 250,
      style: { textAlign: 'left' },
      cell: ({ row }) => <span title={row.original.closestFacilityName ?? ''}>{row.original.closestFacilityName ?? ''}</span>,
    },
    ...(hideReader
      ? []
      : [
          {
            header: 'Reader',
            accessorKey: 'imei',
            size: 130,
            cell: ({ row }) => authorizedLinkTo(AppPermissions.Reader.Read, `/readers/${row.original.imei}`, row.original.imei),
          },
        ]),
    {
      header: 'ULD Status',
      accessorKey: 'equipmentStatus',
      size: 90,
      style: { textAlign: 'center' },
      cell: ({ row }) => <>{row.original.equipmentStatusDescr ?? ''}</>,
    },
    {
      header: 'Network',
      accessorKey: 'networkCarrierCode',
      enableColumnFilter: true,
      size: 60,
      style: { textAlign: 'center' },
    },
    {
      header: 'ULD Cond.',
      accessorKey: 'condition',
      size: 80,
      style: { textAlign: 'center' },
    },
    {
      header: dateHeader('Last Seen'),
      accessorKey: 'lastSeenDateTime',
      size: 110,
      cell: ({ row, table }) => formatCellDate(table, row.original.lastSeenDateTime),
    },
    {
      header: dateHeader('Paired'),
      accessorKey: 'pairedDateTime',
      size: 100,
      cell: ({ row, table }) => formatCellDate(table, row.original.pairedDateTime),
    },
    {
      header: 'Rssi',
      accessorKey: 'rssi',
      size: 40,
      style: { textAlign: 'center' },
    },
    {
      header: 'Battery',
      accessorKey: 'batteryPercent',
      size: 60,
      style: { textAlign: 'center' },
      cell: ({ row }) => {
        const percentage = row.original.batteryPercent ?? 0;
        const color = percentage >= 80 ? 'green' : percentage <= 20 ? 'red' : percentage <= 30 ? 'orange' : '';
        return <span style={{ color: color }}>{percentage}%</span>;
      },
    },
    {
      header: 'Voltage',
      accessorKey: 'batteryVoltage',
      size: 60,
      style: { textAlign: 'center' },
      cell: ({ row }) => <>{row.original.batteryVoltage && <>{row.original.batteryVoltage}</>}</>,
    },
    {
      header: 'Humidity',
      accessorKey: 'humidity',
      size: 65,
    },
    {
      header: 'Temp (C)',
      accessorKey: 'temperatureC',
      size: 70,
    },
    {
      header: 'Light',
      accessorKey: 'light',
      size: 60,
    },
    {
      header: 'Press',
      accessorKey: 'pressure',
      size: 60,
      cell: ({ row }) => <>{row.original.pressure ?? 'None'}</>,
    },
    {
      header: dateHeader('Created'),
      accessorKey: 'createdDateTime',
      size: 110,
      cell: ({ row, table }) => formatCellDate(table, row.original.createdDateTime),
    },
    {
      header: dateHeader('Modified'),
      accessorKey: 'modifiedDateTime',
      size: 'auto',
      cell: ({ row, table }) => formatCellDate(table, row.original.modifiedDateTime),
    },
  ] as Array<any>;

  return col;
};
